import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Boxes, Droplets, LineChart } from "lucide-react";
import { HeartLogoPanel } from "@/components/brand/HeartLogo";

const TILES = [
  {
    id: "supplies",
    eyebrow: "Supplies & Disposables",
    title: "We ship the case cart.",
    blurb:
      "Surgical instrumentation, sterile processing consumables, EVS and janitorial hardware, devices and clinical equipment — sourced, allocated and dispatched nationwide.",
    proof: [
      "No GPO contract required to buy",
      "Backordered & allocated SKUs sourced in 24–48 hrs",
      "Ray-Cot® and FABCO neuro sponges held in stock",
      "Sol-M, BluBox, Aspen Medical & BEAR-ENT authorized lines",
    ],
    to: "/how-it-works/backorder-sourcing",
    cta: "Source a backordered SKU",
    icon: Boxes,
    img: "https://images.pexels.com/photos/5860937/pexels-photo-5860937.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    accent: "cyan",
  },
  {
    id: "infrastructure",
    eyebrow: "Healthcare Infrastructure",
    title: "We build the room it runs in.",
    blurb:
      "Medical-grade water purification, skilled trades, critical environment maintenance and turnkey healthcare construction — from one isolation suite to a complete ASC.",
    proof: [
      "AAMI ST108 Critical Water systems for SPD & dialysis",
      "Portable DI tank exchange on a standing route",
      "Texas-licensed GC + master plumbers in house",
      "ICRA-compliant, occupied-facility work",
    ],
    to: "/solutions/water-treatment",
    cta: "See water & construction",
    icon: Droplets,
    img: "https://images.unsplash.com/photo-1513828742140-ccaa28f3eda0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwd2F0ZXIlMjB0cmVhdG1lbnQlMjByZXZlcnNlJTIwb3Ntb3NpcyUyMHN5c3RlbXxlbnwwfHx8fDE3OTMyODgxMjd8MA&ixlib=rb-4.1.0&q=85",
    accent: "amber",
  },
  {
    id: "software",
    eyebrow: "Healthcare Software",
    title: "We prove where the money went.",
    blurb:
      "IMS bill-only implant management, H.E.A.R.T rebate management and inventory management — built for hospital supply chain, priced for the department that has to justify it.",
    proof: [
      "H.E.A.R.T — Healthcare Earned Advanced Rebate Tracking",
      "Bill-only implant capture from case to invoice",
      "Par-level inventory with usage history",
      "Deploys alongside your ERP, not against it",
    ],
    to: "/solutions/heart",
    cta: "Walk through H.E.A.R.T",
    icon: LineChart,
    img: "https://images.pexels.com/photos/27141316/pexels-photo-27141316.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    brand: "heart",
    accent: "cyan",
  },
];

const ACCENTS = {
  cyan: {
    ring: "hover:border-cyan-400/60",
    chip: "bg-cyan-500/15 border-cyan-400/30 text-cyan-200",
    dot: "bg-cyan-300",
    cta: "text-cyan-300 group-hover:text-cyan-200",
    glow: "group-hover:shadow-[0_28px_70px_-28px_rgba(34,211,238,0.55)]",
  },
  amber: {
    ring: "hover:border-amber-400/60",
    chip: "bg-amber-500/15 border-amber-400/30 text-amber-200",
    dot: "bg-amber-300",
    cta: "text-amber-300 group-hover:text-amber-200",
    glow: "group-hover:shadow-[0_28px_70px_-28px_rgba(251,191,36,0.5)]",
  },
};

const TILE_FADE = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

export function CapabilityTiles() {
  return (
    <section
      className="relative bg-[#061124] border-t border-white/5 py-20 lg:py-28"
      data-testid="capability-tiles-section"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-cyan-500/[0.07] blur-[160px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={TILE_FADE}
          className="max-w-3xl mb-14"
        >
          <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-300">
            Three Businesses. One Vendor Record.
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight">
            What a procurement office can actually buy from us.
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-300/90 leading-relaxed">
            Most health systems hold three separate vendor files for this work. Pick the
            one you came here for &mdash; each is a full operating business, not a line
            item on a capabilities statement.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12 } } }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {TILES.map((tile) => {
            const a = ACCENTS[tile.accent];
            const Icon = tile.icon;
            return (
              <motion.div key={tile.id} variants={TILE_FADE}>
                <Link
                  to={tile.to}
                  data-testid={`capability-tile-${tile.id}`}
                  className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl transition-all duration-500 hover:-translate-y-1.5 ${a.ring} ${a.glow}`}
                >
                  <div className="relative h-44 lg:h-52 overflow-hidden">
                    {tile.brand === "heart" ? (
                      <HeartLogoPanel />
                    ) : (
                      <>
                        <img
                          src={tile.img}
                          alt={tile.eyebrow}
                          loading="lazy"
                          className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-90"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#061124] via-[#061124]/50 to-transparent" />
                      </>
                    )}
                    <div className="absolute left-5 top-5">
                      <span
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] backdrop-blur ${a.chip}`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {tile.eyebrow}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col p-6 lg:p-8">
                    <h3 className="font-display text-2xl lg:text-[1.75rem] font-bold text-white tracking-tight leading-snug">
                      {tile.title}
                    </h3>
                    <p className="mt-3 text-sm md:text-base text-slate-300/85 leading-relaxed">
                      {tile.blurb}
                    </p>

                    <ul className="mt-6 space-y-2.5 border-t border-white/10 pt-6">
                      {tile.proof.map((p) => (
                        <li key={p} className="flex gap-3 text-sm text-slate-200/85 leading-snug">
                          <span
                            aria-hidden="true"
                            className={`mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full ${a.dot}`}
                          />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>

                    <span
                      className={`mt-8 inline-flex items-center gap-2 text-sm font-bold transition-colors ${a.cta}`}
                    >
                      {tile.cta}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

export default CapabilityTiles;
